import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import RightPanel from '../../../components/RightPanel';
import { taskApi, userApi, commentApi, userManagementApi, teamApi } from '../../../services/api';
import RichCommentBox from '../../../components/RichCommentBox';
import { TaskStatus, TaskPriority } from '../../../types/task';
import { showSuccess, showError } from '../../../utils/toast';
import { Download, X, ChevronDown, ChevronUp, Image as ImageIcon, Video as VideoIcon, Music, FileText as FileTextIcon, File as FileIcon, Heart, MessageSquare, Edit2 } from 'lucide-react';
import Skeleton from '../../../components/Skeleton';

interface Props {
  taskId: number;
  onClose: () => void;
  onUpdate?: () => void;
  mode?: 'view' | 'edit' | 'comments';
  onModeChange?: (newMode: 'view' | 'edit' | 'comments') => void;
}

type Attachment = {
  id?: number;
  file_name?: string;
  file_size?: number; // bytes
  mime_type?: string;
  file_path?: string;
  url?: string;
  download_url?: string;
  preview_url?: string;
};

const formatBytes = (bytes?: number) => {
  if (!bytes && bytes !== 0) return '';
  const sizes = ['B','KB','MB','GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(1)} ${sizes[i]}`;
};

const isImage = (att: Attachment) => {
  const mt = att.mime_type || '';
  if (mt.startsWith('image/')) return true;
  const name = att.file_name || '';
  return /(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
};

const API_ORIGIN = 'http://localhost:8000';
const storageUrl = (file_path?: string) => (file_path ? `${API_ORIGIN}/storage/${file_path}` : undefined);

const getUrl = (att: Attachment) => storageUrl(att.file_path) || att.preview_url || att.url || att.download_url || '#';

// helper to derive a short file type label from mime or filename
const getFileTypeLabel = (fileName?: string, mime?: string): string => {
	if (mime) {
		const [type, subtype] = mime.split('/');
		if (type && subtype) {
			if (subtype.includes('pdf')) return 'PDF';
			if (type === 'image') return subtype.toUpperCase();
			if (type === 'video') return 'VIDEO';
			if (type === 'audio') return 'AUDIO';
			return subtype.toUpperCase();
		}
	}
	if (fileName && fileName.includes('.')) {
		const ext = fileName.split('.').pop() || '';
		return ext.toUpperCase();
	}
	return 'FILE';
};

// Lightbox type for richer previews
type LightboxType = 'image' | 'pdf' | 'video' | 'audio' | 'doc';

// Utility: Google Docs Viewer URL for office docs
const googleViewerUrl = (url: string) => `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(url)}`;

// Select an icon component based on mime/ext
const getTypeIcon = (opts: { mime?: string; fileName?: string }) => {
	const { mime, fileName } = opts;
	if (mime?.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName || '')) return <ImageIcon className="w-3 h-3 text-blue-600" />;
	if (mime === 'application/pdf' || /\.pdf$/i.test(fileName || '')) return <FileTextIcon className="w-3 h-3 text-red-600" />;
	if (mime?.startsWith('video/') || /\.(mp4|webm|ogg|mov)$/i.test(fileName || '')) return <VideoIcon className="w-3 h-3 text-purple-600" />;
	if (mime?.startsWith('audio/') || /\.(mp3|wav|ogg|m4a)$/i.test(fileName || '')) return <Music className="w-3 h-3 text-emerald-600" />;
	return <FileIcon className="w-3 h-3 text-gray-600" />;
};

const TaskSidePanel: React.FC<Props> = ({ taskId, onClose, onUpdate, mode = 'comments', onModeChange }) => {
  const [task, setTask] = useState<any | null>(null);
  const [comments, setComments] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(true);
  const [loadingComments, setLoadingComments] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [lightbox, setLightbox] = useState<{ url: string; type: LightboxType } | null>(null);
  const [liking, setLiking] = useState<Record<number, boolean>>({});
  const [repliesExpanded, setRepliesExpanded] = useState<Record<number, boolean>>({});
  const [replyEditorOpen, setReplyEditorOpen] = useState<Record<number, boolean>>({});
  const [repliesByParent, setRepliesByParent] = useState<Record<number, any[]>>({});
  const [loadingReplies, setLoadingReplies] = useState<Record<number, boolean>>({});
  const [activeTab, setActiveTab] = useState<'updates' | 'files'>('updates');
  
  // Edit mode state
  const [editMode, setEditMode] = useState(mode === 'edit');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    task_name: '',
    description: '',
    status: 'Not Started' as TaskStatus,
    priority: 'Normal' as TaskPriority,
    due_date: '',
    assignee_id: '' as number | '',
    assigned_to_team_id: '' as number | '',
  });
  const [users, setUsers] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);

  useEffect(() => {
    // Initialize editMode from mode prop when component mounts or mode changes
    // But don't override if user has manually changed it (e.g., via cancel)
    setEditMode(mode === 'edit');
  }, [mode, taskId]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const t = await taskApi.getById(taskId);
        setTask(t);
        
        // Initialize form with task data
        if (t) {
          setForm({
            task_name: t.task_name || '',
            description: t.description || '',
            status: t.status || 'Not Started',
            priority: t.priority || 'Normal',
            due_date: t.due_date ? t.due_date.split('T')[0] : '',
            assignee_id: t.assignee_id || '',
            assigned_to_team_id: t.assigned_to_team_id || '',
          });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [taskId]);
  
  useEffect(() => {
    if (mode === 'comments') {
      loadComments();
    }
  }, [taskId, mode]);
  
  useEffect(() => {
    const loadUsersAndTeams = async () => {
      try {
        const [usersData, teamsData] = await Promise.all([
          userManagementApi.list({ per_page: 1000 }),
          teamApi.list({ per_page: 1000 }),
        ]);
        setUsers(usersData.data || []);
        setTeams(teamsData.data || []);
      } catch (error) {
        console.error('Failed to load users/teams:', error);
      }
    };
    if (editMode) {
      loadUsersAndTeams();
    }
  }, [editMode]);

  const loadComments = async () => {
    setLoadingComments(true);
    try {
      // Load only top-level comments (parent_id = null)
      const res = await commentApi.getAll(taskId, null);
      const rows = (res.data || []).slice().sort((a:any,b:any)=> new Date(b.created_at||b.createdAt).getTime() - new Date(a.created_at||a.createdAt).getTime());
      setComments(rows);
      // Prime replies cache from payload if present
      const map: Record<number, any[]> = {};
      const open: Record<number, boolean> = {};
      rows.forEach((c:any) => {
        if (Array.isArray(c.replies) && c.replies.length > 0) {
          map[c.id] = c.replies;
          open[c.id] = true; // auto-expand threads that already have replies
        }
      });
      setRepliesByParent(map);
      if (Object.keys(open).length) setRepliesExpanded((prev: Record<number, boolean>) => ({ ...prev, ...open }));
    } finally {
      setLoadingComments(false);
    }
  };
  useEffect(() => { loadComments(); }, [taskId]);

  const handleCommentSubmit = async (payload: { task_id:number; raw_text:string; html:string }) => {
    await commentApi.create(payload);
    await loadComments();
  };
  const handleSearchUsers = async (q:string) => userApi.search(q);

  const handleNewComment = (created?: any) => {
		if (created) {
			setComments(prev => [{ ...created }, ...prev]);
		} else {
			loadComments();
		}
	};

  const toggleLike = async (comment: any) => {
    if (liking[comment.id]) return;
    setLiking(prev => ({ ...prev, [comment.id]: true }));
    const optimisticLiked = !comment.is_liked;
    const prevCount = comment.likes_count || 0;
    setComments(prev => prev.map(c => c.id === comment.id ? { ...c, is_liked: optimisticLiked, likes_count: Math.max(0, prevCount + (optimisticLiked ? 1 : -1)) } : c));
    try {
      const res = optimisticLiked ? await commentApi.like(comment.id) : await commentApi.unlike(comment.id);
      setComments(prev => prev.map(c => c.id === comment.id ? { ...c, is_liked: res.is_liked, likes_count: res.likes_count } : c));
    } catch (e) {
      setComments(prev => prev.map(c => c.id === comment.id ? { ...c, is_liked: !optimisticLiked, likes_count: prevCount } : c));
    } finally {
      setLiking(prev => ({ ...prev, [comment.id]: false }));
    }
  };

  const toggleReplies = async (parentId: number) => {
    setRepliesExpanded(prev => ({ ...prev, [parentId]: !prev[parentId] }));
    const willOpen = !repliesExpanded[parentId];
    if (willOpen && !repliesByParent[parentId]) {
      setLoadingReplies(prev => ({ ...prev, [parentId]: true }));
      try {
        const res = await commentApi.listReplies(parentId);
        setRepliesByParent(prev => ({ ...prev, [parentId]: res.data || [] }));
      } finally {
        setLoadingReplies(prev => ({ ...prev, [parentId]: false }));
      }
    }
  };

  const handleReplyCreated = (parentId: number) => (reply?: any) => {
    if (reply) {
      setRepliesByParent(prev => ({ ...prev, [parentId]: [reply, ...(prev[parentId] || [])] }));
      setReplyEditorOpen(prev => ({ ...prev, [parentId]: false }));
    } else {
      // fallback reload
      toggleReplies(parentId); // close
      toggleReplies(parentId); // reopen and load
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'assignee_id' || name === 'assigned_to_team_id' ? (value ? Number(value) : '') : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    
    try {
      await taskApi.update(taskId, {
        task_name: form.task_name,
        description: form.description || null,
        status: form.status,
        priority: form.priority,
        due_date: form.due_date || null,
        assignee_id: form.assignee_id ? Number(form.assignee_id) : null,
        assigned_to_team_id: form.assigned_to_team_id ? Number(form.assigned_to_team_id) : null,
      });
      
      // Reload task data
      const updatedTask = await taskApi.getById(taskId);
      setTask(updatedTask);
      
      showSuccess('Task updated successfully!');
      setEditMode(false);
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Update failed';
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (task) {
      setForm({
        task_name: task.task_name || '',
        description: task.description || '',
        status: task.status || 'Not Started',
        priority: task.priority || 'Normal',
        due_date: task.due_date ? task.due_date.split('T')[0] : '',
        assignee_id: task.assignee_id || '',
        assigned_to_team_id: task.assigned_to_team_id || '',
      });
    }
    setEditMode(false);
  };

  const header = (
    <div className="min-w-0">
      <p className="text-xs theme-text-secondary font-mono truncate">{task?.task_number || `TASK-${taskId}`}</p>
      <h3 className="text-base font-semibold theme-text-primary truncate">{task?.task_name || task?.title || 'Loading…'}</h3>
    </div>
  );

  const headerExtras = (mode === 'view' || (!editMode && mode === 'edit')) ? (
    <button
      onClick={() => {
        if (onModeChange) {
          onModeChange('edit');
        } else {
          setEditMode(true);
        }
      }}
      className="inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity theme-text-primary"
      title="Edit task"
    >
      <Edit2 className="w-3.5 h-3.5" />
      Edit
    </button>
  ) : undefined;

  const toggleExpand = (key: string) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // utility to open lightbox with type
  const openLightbox = (url: string, type: LightboxType) => setLightbox({ url, type });

  return (
    <RightPanel isOpen={true} onClose={onClose} title={header} headerExtras={headerExtras}>
      {loading ? (
        <div className="space-y-3 p-1">
          <Skeleton height={60} variant="rounded" />
          <div className="grid grid-cols-2 gap-3">
            <Skeleton height={48} variant="rounded" />
            <Skeleton height={48} variant="rounded" />
          </div>
          <Skeleton height={48} variant="rounded" />
          <Skeleton height={48} variant="rounded" />
          <Skeleton height={48} variant="rounded" />
          <Skeleton height={48} variant="rounded" />
        </div>
      ) : (
        <>
          {(mode === 'view' || (!editMode && mode === 'edit')) && (
            <div className="space-y-3">
              <div className="rounded-xl theme-border theme-card p-3">
                <h4 className="text-sm font-semibold theme-text-primary mb-1">Description</h4>
                <p className="text-sm theme-text-primary leading-relaxed">{task?.description || 'No description'}</p>
              </div>
              
              <div className="rounded-xl theme-border theme-card p-3">
                <h4 className="text-sm font-semibold theme-text-primary mb-2">Details</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="theme-text-secondary">Status:</span>
                    <span className="ml-2 font-medium theme-text-primary">{task?.status || '-'}</span>
                  </div>
                  <div>
                    <span className="theme-text-secondary">Priority:</span>
                    <span className="ml-2 font-medium theme-text-primary">{task?.priority || '-'}</span>
                  </div>
                  <div>
                    <span className="theme-text-secondary">Assignee:</span>
                    <span className="ml-2 font-medium theme-text-primary">
                      {task?.assignee?.name || task?.assignedTeam?.name || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="theme-text-secondary">Due Date:</span>
                    <span className="ml-2 font-medium theme-text-primary">
                      {task?.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="theme-text-secondary">Project:</span>
                    <span className="ml-2 font-medium theme-text-primary">{task?.project?.name || '-'}</span>
                  </div>
                  <div>
                    <span className="theme-text-secondary">Created:</span>
                    <span className="ml-2 font-medium theme-text-primary">
                      {task?.created_at ? new Date(task.created_at).toLocaleDateString() : '-'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {editMode && (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-1">Task Name</label>
                <input
                  type="text"
                  name="task_name"
                  value={form.task_name}
                  onChange={handleFormChange}
                  className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-1">Description</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleFormChange}
                  rows={4}
                  className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-1">Status</label>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleFormChange}
                    className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Not Started">Not Started</option>
                    <option value="Deferred">Deferred</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Waiting for input">Waiting for input</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-1">Priority</label>
                  <select
                    name="priority"
                    value={form.priority}
                    onChange={handleFormChange}
                    className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Highest">Highest</option>
                    <option value="High">High</option>
                    <option value="Normal">Normal</option>
                    <option value="Low">Low</option>
                    <option value="Lowest">Lowest</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-1">Due Date</label>
                <input
                  type="date"
                  name="due_date"
                  value={form.due_date}
                  onChange={handleFormChange}
                  className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-1">Assignee</label>
                  <select
                    name="assignee_id"
                    value={form.assignee_id}
                    onChange={handleFormChange}
                    className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Unassigned</option>
                    {users.map((u: any) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-1">Team</label>
                  <select
                    name="assigned_to_team_id"
                    value={form.assigned_to_team_id}
                    onChange={handleFormChange}
                    className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">No team</option>
                    {teams.map((t: any) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-3 py-1.5 text-sm theme-border rounded theme-bg-secondary theme-text-primary hover:opacity-80"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-3 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded hover:shadow-lg transition-shadow disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          )}

          {mode === 'comments' && (
            <>
              <div className="rounded-xl theme-border theme-card p-3">
                <h4 className="text-sm font-semibold theme-text-primary mb-1">Description</h4>
                <p className="text-sm theme-text-primary leading-relaxed">{task?.description || 'No description'}</p>
              </div>
              
              {/* Tabs */}
              <div className="mt-3">
                <div className="flex items-center gap-2 border-b theme-border">
                  <button
                    className={`px-3 py-2 text-sm font-medium ${activeTab==='updates' ? 'text-blue-600 border-b-2 border-blue-600' : 'theme-text-secondary hover:theme-text-primary'}`}
                    onClick={() => setActiveTab('updates')}
                  >
                    Updates
                  </button>
                  <button
                    className={`px-3 py-2 text-sm font-medium ${activeTab==='files' ? 'text-blue-600 border-b-2 border-blue-600' : 'theme-text-secondary hover:theme-text-primary'}`}
                    onClick={() => setActiveTab('files')}
                  >
                    Files
                  </button>
                </div>
              </div>
            </>
          )}

          {mode === 'comments' && activeTab === 'updates' && (
            <div className="rounded-xl theme-border theme-card p-3">
              <h4 className="text-sm font-semibold theme-text-primary mb-2">Comments & Mentions</h4>
              <RichCommentBox
                taskId={taskId}
                onCommentCreated={handleNewComment}
              />

              <div className="mt-4 space-y-3">
                {loadingComments && <div className="text-xs theme-text-tertiary">Loading comments…</div>}
                {comments.map((c:any) => (
                <div key={c.id} className="rounded-lg theme-border theme-card p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full text-white text-[10px] font-bold flex items-center justify-center">
                      {(c.author?.name || 'U').split(' ').map((w:string)=>w[0]).slice(0,2).join('')}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium theme-text-primary truncate">{c.author?.name || 'Unknown'}</p>
                      <p className="text-[10px] theme-text-secondary">{new Date(c.created_at || c.createdAt).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="prose prose-sm max-w-none theme-text-primary mb-2" dangerouslySetInnerHTML={{ __html: c.html || c.content }} />

                  {/* Actions: like & reply */}
                  <div className="flex items-center gap-4 mb-2 text-xs">
                    <button
                      onClick={() => toggleLike(c)}
                      disabled={!!liking[c.id]}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border ${c.is_liked ? 'bg-rose-50 border-rose-200 text-rose-600' : 'theme-card theme-border theme-text-secondary'} hover:opacity-80`}
                    >
                      <Heart className={`w-3.5 h-3.5 ${c.is_liked ? 'fill-rose-500 stroke-rose-500' : ''}`} />
                      <span>{c.likes_count ?? 0}</span>
                    </button>
                    <button
                      onClick={() => {
                        // open editor only; ensure replies section visible and loaded
                        setReplyEditorOpen(prev => ({ ...prev, [c.id]: !prev[c.id] }));
                        if (!repliesExpanded[c.id]) toggleReplies(c.id);
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border theme-card theme-border theme-text-secondary hover:opacity-80"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      <span>Reply</span>
                    </button>
                  </div>

                  {Array.isArray(c.attachments) && c.attachments.length > 0 && (
                    <div className="mt-2 space-y-2">
                      <p className="text-xs font-semibold theme-text-primary">Attachments:</p>
                      {c.attachments.map((att: Attachment, idx: number) => {
                        const key = `${c.id}-${att.id ?? idx}`;
                        const expandedNow = !!expanded[key];
                        const fileName = att.file_name || (att as any).filename || 'file';
                        const typeLabel = getFileTypeLabel(fileName, att.mime_type as any);
                        const url = storageUrl(att.file_path) || att.download_url || att.url || '';
                        const mime = (att as any).mime_type as string | undefined;
                        const isImageFile = mime?.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName);
                        const isPdfFile = mime === 'application/pdf' || /\.pdf$/i.test(fileName);
                        const isVideoFile = mime?.startsWith('video/') || /\.(mp4|webm|ogg|mov)$/i.test(fileName);
                        const isAudioFile = mime?.startsWith('audio/') || /\.(mp3|wav|ogg|m4a)$/i.test(fileName);
                        const isDocFile = (
                          /application\/(msword|vnd\.openxmlformats-officedocument\.(wordprocessingml\.document|presentationml\.presentation|spreadsheetml\.sheet)|vnd\.ms-(powerpoint|excel))/.test(mime || '')
                          || /\.(docx?|pptx?|xlsx?)$/i.test(fileName)
                          || mime === 'text/plain' || /\.txt$/i.test(fileName)
                        );
                        return (
                          <div key={att.id ?? idx} className="rounded-md theme-border theme-card">
                            <div className="flex items-center justify-between px-3 py-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                                <span className="text-xs theme-text-primary truncate max-w-[220px]">{fileName}</span>
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border theme-border theme-bg-secondary theme-text-primary">
                                  {getTypeIcon({ mime, fileName })}
                                  {typeLabel}
                                </span>
                                <span className="text-[10px] theme-text-tertiary">{formatBytes((att as any).file_size || 0)}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                {(isImageFile || isPdfFile || isVideoFile || isAudioFile || isDocFile) && (
                                  <button onClick={()=> toggleExpand(key)} className="theme-text-primary hover:text-blue-600" title="Toggle preview">
                                    {expandedNow ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                  </button>
                                )}
                                <a href={url || '#'} target="_blank" rel="noreferrer" className="theme-text-primary hover:text-blue-600" title="Download">
                                  <Download className="w-4 h-4" />
                                </a>
                              </div>
                            </div>
                            {expandedNow && (
                              <div className="px-3 pb-3">
                                {isImageFile && (
                                  <img
                                    src={url}
                                    alt={fileName}
                                    className="mt-2 max-h-40 rounded-md theme-border cursor-zoom-in"
                                    onClick={()=> openLightbox(url, 'image')}
                                  />
                                )}
                                {isPdfFile && (
                                  <div className="mt-2">
                                    <iframe src={url} title={fileName} className="w-full h-60 rounded-md theme-border theme-bg-primary" />
                                    <div className="mt-2 flex justify-end">
                                      <button onClick={()=> openLightbox(url, 'pdf')} className="px-2 py-1 text-xs rounded-md theme-border theme-card hover:opacity-80 theme-text-primary">Open in Lightbox</button>
                                    </div>
                                  </div>
                                )}
                                {isVideoFile && (
                                  <div className="mt-2">
                                    <video src={url} controls className="w-full max-h-60 rounded-md theme-border bg-black" />
                                    <div className="mt-2 flex justify-end">
                                      <button onClick={()=> openLightbox(url, 'video')} className="px-2 py-1 text-xs rounded-md theme-border theme-card hover:opacity-80 theme-text-primary">Open in Lightbox</button>
                                    </div>
                                  </div>
                                )}
                                {isAudioFile && (
                                  <div className="mt-2">
                                    <audio src={url} controls className="w-full" />
                                  </div>
                                )}
                                {isDocFile && (
                                  <div className="mt-2">
                                    <iframe src={googleViewerUrl(url)} title={fileName} className="w-full h-60 rounded-md theme-border theme-bg-primary" />
                                    <div className="mt-2 flex justify-end">
                                      <button onClick={()=> openLightbox(googleViewerUrl(url), 'doc')} className="px-2 py-1 text-xs rounded-md theme-border theme-card hover:opacity-80 theme-text-primary">Open in Lightbox</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Replies section */}
                  {repliesExpanded[c.id] && (
                    <div className="mt-2 pl-4 border-l-2 theme-border space-y-3">
                      {replyEditorOpen[c.id] && (
                        <>
                          <RichCommentBox taskId={taskId} parentId={c.id} onCommentCreated={handleReplyCreated(c.id)} />
                          <div className="flex justify-end">
                            <button
                              onClick={() => setReplyEditorOpen(prev => ({ ...prev, [c.id]: false }))}
                              className="mt-2 px-3 py-1.5 text-xs rounded-lg theme-card theme-border theme-text-primary hover:opacity-80"
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      )}
                      {loadingReplies[c.id] && <div className="text-[11px] theme-text-tertiary">Loading replies…</div>}
                      {(repliesByParent[c.id] || []).map((r:any) => (
                        <div key={r.id} className="rounded-md theme-border theme-card p-2">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full text-white text-[9px] font-bold flex items-center justify-center">
                              {(r.author?.name || 'U').split(' ').map((w:string)=>w[0]).slice(0,2).join('')}
                            </div>
                            <div className="min-w-0">
                              <p className="text-[11px] font-medium theme-text-primary truncate">{r.author?.name || 'Unknown'}</p>
                              <p className="text-[10px] theme-text-secondary">{new Date(r.created_at || r.createdAt).toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="prose prose-xs max-w-none theme-text-primary" dangerouslySetInnerHTML={{ __html: r.html || r.content }} />
                          <div className="mt-1 text-[10px] theme-text-secondary inline-flex items-center gap-1">
                            <Heart className={`w-3 h-3 ${r.is_liked ? 'fill-rose-500 stroke-rose-500' : ''}`} />
                            <span>{r.likes_count ?? 0}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                </div>
                ))}
                {!loadingComments && comments.length === 0 && (
                  <div className="text-xs theme-text-tertiary">No comments yet.</div>
                )}
              </div>
            </div>
          )}

          {mode === 'comments' && activeTab === 'files' && (
            <div className="rounded-xl theme-border theme-card p-3">
              <h4 className="text-sm font-semibold theme-text-primary mb-2">Files</h4>
              <FilesList
                comments={comments}
                expanded={expanded}
                setExpanded={setExpanded}
                openLightbox={openLightbox}
              />
            </div>
          )}
        </>
      )}

      {/* Lightbox */}
      {lightbox && createPortal(
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center" onClick={()=>setLightbox(null)}>
          <button aria-label="Close" className="absolute top-4 right-4 p-2 rounded-md bg-white/80 hover:bg-white shadow" onClick={()=>setLightbox(null)}>
            <X className="w-5 h-5 text-gray-800" />
          </button>
          {lightbox.type === 'image' && (
            <img src={lightbox.url} alt="preview" className="max-h-[90vh] max-w-[90vw] rounded-lg border border-white/20" />
          )}
          {lightbox.type === 'pdf' && (
            <iframe src={lightbox.url} title="PDF preview" className="w-[90vw] h-[90vh] rounded-lg bg-white" />
          )}
          {lightbox.type === 'video' && (
            <video src={lightbox.url} controls autoPlay className="w-[90vw] max-h-[90vh] rounded-lg bg-black" />
          )}
          {lightbox.type === 'audio' && (
            <div className="w-[80vw] max-w-xl bg-white rounded-lg p-6">
              <audio src={lightbox.url} controls autoPlay className="w-full" />
            </div>
          )}
          {lightbox.type === 'doc' && (
            <iframe src={lightbox.url} title="Document preview" className="w-[90vw] h-[90vh] rounded-lg bg-white" />
          )}
        </div>,
        document.body
      )}
    </RightPanel>
  );
};

export default TaskSidePanel;

// Aggregated Files view for the Files tab
const FilesList: React.FC<{
  comments: any[];
  expanded: Record<string, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  openLightbox: (url: string, type: LightboxType) => void;
}> = ({ comments, expanded, setExpanded, openLightbox }) => {
  const allAttachments: Array<{
    key: string;
    fileName: string;
    url: string;
    size: number;
    mime?: string;
  }> = [];

  const pushFrom = (parentId: number, list: any[]) => {
    list.forEach((att: any, idx: number) => {
      const fileName = att.file_name || att.filename || 'file';
      const url = storageUrl(att.file_path) || att.download_url || att.url || '';
      allAttachments.push({
        key: `${parentId}-${att.id ?? idx}`,
        fileName,
        url,
        size: (att.file_size as number) || (att.size as number) || 0,
        mime: att.mime_type as string | undefined,
      });
    });
  };

  comments.forEach((c:any) => {
    if (Array.isArray(c.attachments)) pushFrom(c.id, c.attachments);
    if (Array.isArray(c.replies)) {
      c.replies.forEach((r:any) => {
        if (Array.isArray(r.attachments)) pushFrom(r.id, r.attachments);
      });
    }
  });

  const toggleExpand = (key: string) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  if (allAttachments.length === 0) {
    return <div className="text-xs theme-text-tertiary">No files yet.</div>;
  }

  return (
    <div className="space-y-2">
      {allAttachments.map(att => {
        const { key, fileName, url, size, mime } = att;
        const expandedNow = !!expanded[key];
        const isImageFile = (mime?.startsWith('image/') || /(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName));
        const isPdfFile = (mime === 'application/pdf' || /\.pdf$/i.test(fileName));
        const isVideoFile = (mime?.startsWith('video/') || /(mp4|webm|ogg|mov)$/i.test(fileName));
        const isAudioFile = (mime?.startsWith('audio/') || /(mp3|wav|ogg|m4a)$/i.test(fileName));
        const isDocFile = (
          /application\/(msword|vnd\.openxmlformats-officedocument\.(wordprocessingml\.document|presentationml\.presentation|spreadsheetml\.sheet)|vnd\.ms-(powerpoint|excel))/.test(mime || '')
          || /\.(docx?|pptx?|xlsx?)$/i.test(fileName)
          || mime === 'text/plain' || /\.(txt)$/i.test(fileName)
        );
        const typeLabel = getFileTypeLabel(fileName, mime as any);
        return (
          <div key={key} className="rounded-md theme-border theme-card">
            <div className="flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                <span className="text-xs theme-text-primary truncate max-w-[220px]">{fileName}</span>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border theme-border theme-bg-secondary theme-text-primary">
                  {getTypeIcon({ mime, fileName })}
                  {typeLabel}
                </span>
                <span className="text-[10px] theme-text-tertiary">{formatBytes(size)}</span>
              </div>
              <div className="flex items-center gap-3">
                {(isImageFile || isPdfFile || isVideoFile || isAudioFile || isDocFile) && (
                  <button onClick={()=> toggleExpand(key)} className="theme-text-primary hover:text-blue-600" title="Toggle preview">
                    {expandedNow ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                )}
                <a href={url || '#'} target="_blank" rel="noreferrer" className="theme-text-primary hover:text-blue-600" title="Download">
                  <Download className="w-4 h-4" />
                </a>
              </div>
            </div>
            {expandedNow && (
              <div className="px-3 pb-3">
                {isImageFile && (
                  <img
                    src={url}
                    alt={fileName}
                    className="mt-2 max-h-40 rounded-md theme-border cursor-zoom-in"
                    onClick={()=> openLightbox(url, 'image')}
                  />
                )}
                {isPdfFile && (
                  <div className="mt-2">
                    <iframe src={url} title={fileName} className="w-full h-60 rounded-md theme-border theme-bg-primary" />
                    <div className="mt-2 flex justify-end">
                      <button onClick={()=> openLightbox(url, 'pdf')} className="px-2 py-1 text-xs rounded-md theme-border theme-card hover:opacity-80 theme-text-primary">Open in Lightbox</button>
                    </div>
                  </div>
                )}
                {isVideoFile && (
                  <div className="mt-2">
                    <video src={url} controls className="w-full max-h-60 rounded-md theme-border bg-black" />
                    <div className="mt-2 flex justify-end">
                      <button onClick={()=> openLightbox(url, 'video')} className="px-2 py-1 text-xs rounded-md theme-border theme-card hover:opacity-80 theme-text-primary">Open in Lightbox</button>
                    </div>
                  </div>
                )}
                {isAudioFile && (
                  <div className="mt-2">
                    <audio src={url} controls className="w-full" />
                  </div>
                )}
                {isDocFile && (
                  <div className="mt-2">
                    <iframe src={googleViewerUrl(url)} title={fileName} className="w-full h-60 rounded-md theme-border theme-bg-primary" />
                    <div className="mt-2 flex justify-end">
                      <button onClick={()=> openLightbox(googleViewerUrl(url), 'doc')} className="px-2 py-1 text-xs rounded-md theme-border theme-card hover:opacity-80 theme-text-primary">Open in Lightbox</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
